import { ValueFormatterParams } from "ag-grid-community";
import { AxiosError } from "axios";
import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import transferIcon from "../../../../assets/images/transferIcon.svg";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Grid from "../../../../components/Grid";
import { ColumnDef } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import ModalConfirm from "../../../../components/ModalConfirm";
import Loading from "../../../../core/common/Loading";
import api from "../../../../services/api";
import { useAuth } from "../../../../hooks/AuthenticateContext";

import { useModal } from "../../../../hooks/ModalContext";
import CreateOperacaoPatioGuiches from "./Create";
import Info from "./Info";
import { IOperacoesPatioGuiches } from "./types/types";
import CustomModal from "../../../../components/CustomModal";
import ListOperacoesPatioGuichesMovimentacao from "../OperacoesPatioGuichesMovimentacao";

type ApiErrorResponse = {
  error_msg?: string;
};

const getCreateGuicheErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError<ApiErrorResponse>;
  return axiosError.response?.data?.error_msg || "Não foi possível cadastrar o guichê.";
};

const ListOperacoesPatioGuiches: React.FC = () => {
  const gridRef = useRef<any>(null);
  const [columns] = useState<ColumnDef[]>([
    {
      field: "numero_guiche",
      headerName: "Número do Guichê",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "usuario_movimentacao",
      fieldName: "usuario_movimentacao",
      headerName: "Usuário Movimentação",
      filter: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "usuario.usuario",
      headerName: "Usuário Cadastro",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
  ]);
  const [selectedRow, setSelectedRow] = useState<IOperacoesPatioGuiches>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [isCreateConfirm, setIsCreateConfirm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMovimentacoesOpen, setIsMovimentacoesOpen] = useState<boolean>(false);
  const [selectedGuicheId, setSelectedGuicheId] = useState<number | null>(null);
  const { user } = useAuth();

  const { isModalOpen, closeModal } = useModal();

  const handleCreate = useCallback(async () => {
    try {
      setLoading(true);

      const body = {
        id_usuario_historico: user?.id,
        ativo: true,
      };

      await api.post("/cadastrar/operacaoPatioGuiches", body);

      setIsCreateConfirm(false);
      gridRef.current?.refresh();
    } catch (error: unknown) {
      toast.warning(getCreateGuicheErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [user]);

  const onDelete = useCallback(async (entityId?: number) => {
    try {
      setLoading(true);

      const body = {
        id_operacao_patio_guiche: entityId,
      };

      await api.post("/deletar/operacaoPatioGuiches", body);

      setLoading(false);
      setIsRemove(false);
      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, []);

  return (
    <>
      <Loading loading={loading} />
      {isModalOpen && (
        <CreateOperacaoPatioGuiches
          isView={isView}
          isEdit={isEdit}
          selectedRow={selectedRow}
          onClear={() => {
            closeModal();
            setIsEdit(false);
          }}
          onConfirm={() => {
            closeModal();
            gridRef.current?.refresh();
          }}
        />
      )}

      {isCreateConfirm && (
        <ModalConfirm
          title="Cadastrar"
          message="Deseja realmente criar um novo guichê?"
          onCancel={() => setIsCreateConfirm(false)}
          onConfirm={handleCreate}
        />
      )}

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_operacao_patio_guiche)}
          row={String(selectedRow?.numero_guiche)}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Operações Pátio Guichês"
          onClose={() => setIsView(!isView)}
        />
      )}

      {isMovimentacoesOpen && (
        <CustomModal
          title="Movimentações"
          onClose={() => setIsMovimentacoesOpen(false)}
          isScreenXl
        >
          <ListOperacoesPatioGuichesMovimentacao
            guicheId={selectedGuicheId}
            isModal
          />
        </CustomModal>
      )}

      <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">Guichês</h1>
          </div>
          <div className="mr-14">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
              onClick={() => {
                setIsCreateConfirm(true);
              }}
            >
              <img src={PlusButtonIcon} className="mr-2" alt="" />
              Adicionar
            </button>
          </div>
        </div>
        <div className="flex w-full">
          <Grid
            ref={gridRef}
            columns={columns}
            filters={[]}
            pagination
            path="/listar/operacaoPatioGuiches"
            onView={(data: IOperacoesPatioGuiches) => {
              setSelectedRow(data);
              setIsView(!isView);
            }}
            onDelete={(data: IOperacoesPatioGuiches) => {
              setSelectedRow(data);
              setIsRemove(!isRemove);
            }}
            customButtons={[
              {
                label: "Movimentações",
                icon: () => transferIcon,
                action: (data: IOperacoesPatioGuiches) => {
                  setSelectedGuicheId(data.id_operacao_patio_guiche);
                  setIsMovimentacoesOpen(true);
                },
              },
            ]}
            status={[]}
          />
        </div>
      </div>
    </>
  );
};

export default ListOperacoesPatioGuiches;
